import { Button, Form, Input, message, Space, Typography } from "antd";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { openNotification } from "../../Components/Notification";
import { useLoader } from "../../Context/LoaderContext";
import AuthService from "../../Service/auth.service";
import Checkbox from "antd/es/checkbox/Checkbox";
import "./index.scss";
import googleAnalyticService from "../../Service/google.analytic.service";

declare const window: any;
const SignUpForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const screeningSessionId = searchParams.get("screening_session_id"); // Phase 7: Read screening session ID
  const { setLoading, setLoadingMessage } = useLoader();

  const [isChecked, setChecked] = useState(false);
  const authService = AuthService.getInstance();

  const handleChange = () => {
    setChecked((check) => (check === true ? false : true));
  };

  const emitGoogleAnalyticsEvent = (orgName: string, email: string) => {
    googleAnalyticService.handleSubmissionEvents({
      category: "Organization Sign up",
      action: "organization_signup",
      organization_email: email,
      organization_name: orgName,
      label: `New Organization : ${orgName} signed up`,
    });
  };

  const handleSignUp = async (formValues: any) => {
    try {
      setLoading(true);
      setLoadingMessage("Signing up please wait...");
      emitGoogleAnalyticsEvent(formValues.orgName, formValues.email);
      // Trim all string values and normalize email to lowercase
      const data = {
        firstName: formValues.firstName?.trim(),
        lastName: formValues.lastName?.trim(),
        orgName: formValues.orgName?.trim(),
        email: formValues.email?.trim().toLowerCase(),
        password: formValues.password?.trim(),
      };
      // Phase 7: Pass screening_session_id if present
      const response = await authService.orgSignUp(
        data,
        screeningSessionId || undefined
      );
      if (response?.data) {
        if (response?.data?.statusCode === 400) {
          message.error(response?.data?.message);
          return;
        } else if (response?.status === 201) {
          if (typeof window.lintrk === "function") {
            window.lintrk("track", { conversion_id: 19052020 });
          }

          const signInResponse = await authService.signIn({
            email: data.email,
            password: data.password,
          });

          message.success("Account created successfully");
          if (signInResponse?.data?.access_token) {
            // Phase 7: Store screening session ID so PreLoad can trigger the job post creation flow
            if (screeningSessionId) {
              sessionStorage.setItem("pending_screening_session_id", screeningSessionId);
              // Determine case type from cta_type query param
              const ctaType = searchParams.get("cta_type");
              // All cta_types use the form-based JD wizard
              sessionStorage.setItem("screening_case_type", "form-based");
              // Also store the raw cta_type so the onboarding screen can show context-specific messaging
              if (ctaType) {
                sessionStorage.setItem("screening_cta_type", ctaType);
              }
            }
            const url = redirect
              ? `/pre-load?redirect=${encodeURIComponent(redirect)}`
              : "/pre-load";
            navigate(url);
          } else {
            const signInUrl = redirect
              ? `/sign-in?redirect=${encodeURIComponent(redirect)}`
              : "/sign-in";
            navigate(signInUrl);
          }
        }
      }
    } catch (err) {
      console.error("Unable to create your account:", err);
      message.error("Unable to create your account. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  return (
    <Form
      name="org_signup"
      initialValues={{ remember: true }}
      onFinish={handleSignUp}
      autoComplete="true"
      scrollToFirstError
      size="large"
    >
      <Form.Item
        name="firstName"
        rules={[
          { required: true, message: "Please enter your first name!" },
          {
            pattern: /^[A-Za-z\s]{2,50}$/,
            message:
              "First name must contain only letters and be 2-50 characters long!",
          },
        ]}
      >
        <Input placeholder="First name" />
      </Form.Item>
      <Form.Item
        name="lastName"
        // rules={[
        //   { required: true, message: "Please enter your last name!" },
        //   {
        //     pattern: /^[A-Za-z\s]{2,50}$/,
        //     message:
        //       "Last name must contain only letters and be 2-50 characters long!",
        //   },
        // ]}
      >
        <Input placeholder="Last name" />
      </Form.Item>
      <Form.Item
        name="orgName"
        rules={[
          {
            required: true,
            message: "Please enter your organisation name!",
          },
          {
            pattern: /^[A-Za-z0-9\s\-&.,'()]{2,50}$/,
            message: "Organisation name must be 2-50 characters long!",
          },
        ]}
      >
        <Input placeholder="Organisation name" />
      </Form.Item>
      <Form.Item
        name="email"
        rules={[
          {
            type: "email",
            message: "The input is not valid E-mail!",
          },
          {
            required: true,
            message: "Please enter your email!",
          },
          {
            pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: "Please enter a valid email address!",
          },
        ]}
      >
        <Input placeholder="Email" />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          {
            required: true,
            message: "Please enter your password!",
          },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();

              const errors = [];
              if (value.length < 8) {
                errors.push("At least 8 characters");
              }
              if (!/[A-Z]/.test(value)) {
                errors.push("One uppercase letter");
              }
              if (!/[a-z]/.test(value)) {
                errors.push("One lowercase letter");
              }
              if (!/[0-9]/.test(value)) {
                errors.push("One number");
              }
              if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                errors.push("One special character");
              }

              if (errors.length > 0) {
                return Promise.reject(
                  new Error(`Password must contain: ${errors.join(", ")}`)
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input.Password placeholder="Password" />
      </Form.Item>
      <Form.Item
        name="confirm"
        dependencies={["password"]}
        rules={[
          {
            required: true,
            message: "Please confirm your password!",
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error("The new password that you entered do not match!")
              );
            },
          }),
        ]}
      >
        <Input.Password placeholder="Confirm Password" />
      </Form.Item>
      <Space direction="vertical" style={{ width: "100%" }} size={16}>
        <Checkbox checked={isChecked} onChange={handleChange}>
          <div style={{ marginLeft: 8 }}>
            I have read and agreed to{" "}
            <a
              href="https://www.qualifyme.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              T & C's
            </a>
            ,{" "}
            <a
              href="https://www.qualifyme.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            ,{" and "}
            <a
              href="https://www.qualifyme.ai/refund"
              target="_blank"
              rel="noopener noreferrer"
            >
              Refund Policy.
            </a>
          </div>
        </Checkbox>
        <Button
          type="primary"
          htmlType="submit"
          block
          disabled={isChecked === false}
        >
          Sign Up
        </Button>
        <Typography.Paragraph style={{ textAlign: "center" }}>
          Already have an account?
          <span className="login-text">
            <Typography.Link
              onClick={() =>
                navigate(
                  `/sign-in?redirect=${encodeURIComponent(redirect || "/")}`
                )
              }
            >
              Sign In
            </Typography.Link>
          </span>
        </Typography.Paragraph>
      </Space>
    </Form>
  );
};

export default SignUpForm;
